import { useEditorState, type Editor } from '@tiptap/react';
import type { ReactNode } from 'react';
import {
  LuAlignCenter,
  LuAlignLeft,
  LuAlignRight,
  LuBetweenHorizontalEnd,
  LuBetweenHorizontalStart,
  LuBetweenVerticalEnd,
  LuBetweenVerticalStart,
  LuPanelTop,
  LuRotateCcw,
  LuTableCellsMerge,
  LuTableCellsSplit,
  LuTrash2,
} from '@platform/icon';
import type { ImageAlign } from './editorFormatting.ts';
import { ToolButton, ToolDivider } from './EditorToolbarParts.tsx';

const ICON = 'size-4';

function ContextBar({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div
      role="toolbar"
      aria-label={title}
      className="flex flex-wrap items-center gap-0.5 border-t border-black/10 bg-white/70 px-2 py-1 dark:border-white/10 dark:bg-white/[0.03]"
    >
      <span className="mr-1 px-1 text-xs font-semibold text-neutral-500 dark:text-neutral-400">{title}</span>
      {children}
    </div>
  );
}

/** 커서가 표 안에 있을 때 — 행/열 삽입·삭제, 셀 병합/분할, 머리글 행 */
export function TableContextBar({ editor }: { editor: Editor }) {
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      canMerge: e.can().mergeCells(),
      canSplit: e.can().splitCell(),
    }),
  });
  const chain = () => editor.chain().focus();

  return (
    <ContextBar title="표">
      <ToolButton label="위에 행 삽입" onClick={() => chain().addRowBefore().run()}>
        <LuBetweenHorizontalStart className={ICON} />
      </ToolButton>
      <ToolButton label="아래에 행 삽입" onClick={() => chain().addRowAfter().run()}>
        <LuBetweenHorizontalEnd className={ICON} />
      </ToolButton>
      <ToolButton label="왼쪽에 열 삽입" onClick={() => chain().addColumnBefore().run()}>
        <LuBetweenVerticalStart className={ICON} />
      </ToolButton>
      <ToolButton label="오른쪽에 열 삽입" onClick={() => chain().addColumnAfter().run()}>
        <LuBetweenVerticalEnd className={ICON} />
      </ToolButton>
      <ToolDivider />
      <ToolButton label="행 삭제" onClick={() => chain().deleteRow().run()}>
        <span className="px-0.5 text-xs">행 삭제</span>
      </ToolButton>
      <ToolButton label="열 삭제" onClick={() => chain().deleteColumn().run()}>
        <span className="px-0.5 text-xs">열 삭제</span>
      </ToolButton>
      <ToolDivider />
      <ToolButton label="셀 병합 (여러 셀을 끌어 선택)" disabled={!s.canMerge} onClick={() => chain().mergeCells().run()}>
        <LuTableCellsMerge className={ICON} />
      </ToolButton>
      <ToolButton label="셀 분할" disabled={!s.canSplit} onClick={() => chain().splitCell().run()}>
        <LuTableCellsSplit className={ICON} />
      </ToolButton>
      <ToolButton label="머리글 행 켜기/끄기" onClick={() => chain().toggleHeaderRow().run()}>
        <LuPanelTop className={ICON} />
      </ToolButton>
      <ToolDivider />
      <ToolButton label="표 삭제" onClick={() => chain().deleteTable().run()}>
        <LuTrash2 className={`${ICON} text-red-600 dark:text-red-400`} />
      </ToolButton>
    </ContextBar>
  );
}

/** 이미지를 선택했을 때 — 정렬·원래 크기·삭제. 크기는 모서리 핸들을 끌어 조절한다. */
export function ImageContextBar({ editor }: { editor: Editor }) {
  const align = useEditorState({
    editor,
    selector: ({ editor: e }) => (e.getAttributes('image').align as ImageAlign | null) ?? 'left',
  });
  const setAlign = (value: ImageAlign) =>
    editor.chain().focus().updateAttributes('image', { align: value === 'left' ? null : value }).run();

  return (
    <ContextBar title="이미지">
      <ToolButton label="왼쪽 정렬" active={align === 'left'} onClick={() => setAlign('left')}>
        <LuAlignLeft className={ICON} />
      </ToolButton>
      <ToolButton label="가운데 정렬" active={align === 'center'} onClick={() => setAlign('center')}>
        <LuAlignCenter className={ICON} />
      </ToolButton>
      <ToolButton label="오른쪽 정렬" active={align === 'right'} onClick={() => setAlign('right')}>
        <LuAlignRight className={ICON} />
      </ToolButton>
      <ToolDivider />
      <ToolButton
        label="원래 크기"
        onClick={() => editor.chain().focus().updateAttributes('image', { width: null, height: null }).run()}
      >
        <LuRotateCcw className={ICON} />
      </ToolButton>
      <ToolButton label="이미지 삭제" onClick={() => editor.chain().focus().deleteSelection().run()}>
        <LuTrash2 className={`${ICON} text-red-600 dark:text-red-400`} />
      </ToolButton>
      <span className="ml-1 hidden text-xs text-neutral-500 sm:inline dark:text-neutral-400">
        모서리를 끌어 크기 조절
      </span>
    </ContextBar>
  );
}
